import {Router} from '@angular/router';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {CountryModel} from '@shared/models/country.model';
import {
  QuickSelectionModel, TreeItemNode, QuickSelectionModelId, SelectionEventModel
} from '@shared/models/tree-selector.models';
import {
  EntitySettingsAddEntityCommand, EntitySettingsDeleteEntityCommand, ResetEntitiesFromProjectSettingsCommand
} from '../../commands/entity-chart.commands';
import {BehaviorSubject, combineLatest, Observable, of, Subject, zip} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {EntitySetting, EntitySettingGroupModel} from '../../../models/entity-setting.model';
import {EntityChartState} from '../../store/reducers/entity-chart.reducer';
import {
  GetEntityCountByCountryAction,
  GetEntityFilterGroupByRegionAction,
  GetEntitySettingsAction,
  PostEntitySettingsAddEntity,
  PostEntitySettingsDeleteEntity,
  ResetEntitiesFromProjectSettingsAction
} from '../../store/actions/entity-chart.actions';
import {
  getChangeReportEntityFilterByGroup, getEntityChartSettings,
  getEntityCountByCountry
} from '../../store/selectors/entity-chart.selector';
import {CombineRoute} from '@shared/constants/RelativePath';
import {ParticleService} from "@core/services/particle.service";
import {CountryTreeSelectorItemViewerComponent} from "../country-tree-selector-item-viewer/country-tree-selector-item-viewer.component";

@Component({
    standalone: false,
    selector: 'lib-app-entity-chart-content',
  templateUrl: './entity-chart-content.component.html',
  styleUrls: ['./entity-chart-content.component.scss']
})
export class EntityChartContentComponent implements OnInit, OnDestroy {
  isParentNodeCollapsible$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  countrySelectorData$: BehaviorSubject<TreeItemNode[]> = new BehaviorSubject([]);
  countryQuickSelections$: Observable<QuickSelectionModel[]>;
  currentTabIndex = 0;
  countryFilterTabs = ['A-Z Countries', 'By Region'];
  entities: EntitySetting[];
  entitiesByGroup: EntitySettingGroupModel[];
  itemViewerName = 'FilterTreeSelectorItemViewer';
  entitybyCountry: any;
  private ngUnsubscribe = new Subject<void>();

  constructor(private store: Store<EntityChartState>, private router: Router, private particleService: ParticleService) {

    particleService.registerCellComponent('FilterTreeSelectorItemViewer', CountryTreeSelectorItemViewerComponent);
    this.countrySelectorData$ = new BehaviorSubject([]);

    this.countryQuickSelections$ = of([{
      name: 'All countries', id: QuickSelectionModelId.ALL
    }, {
      name: 'None', id: QuickSelectionModelId.NONE
    }]);
  }

  countryFilterTitle = 'Countries';

  ngOnInit() {
    this.currentTabIndex = 0;
    this.store.dispatch(new GetEntitySettingsAction());
    this.store.dispatch(new GetEntityCountByCountryAction());
    const getEntityByCountry$ = this.store.pipe(select(getEntityCountByCountry), takeUntil(this.ngUnsubscribe));
    const getEntityChartSettings$ = this.store.select(getEntityChartSettings).pipe(takeUntil(this.ngUnsubscribe));
    const getChangeReportEntityFilterByGroup$ = this.store.select(getChangeReportEntityFilterByGroup).pipe(distinctUntilChanged());
    combineLatest(getEntityByCountry$, getEntityChartSettings$, getChangeReportEntityFilterByGroup$).subscribe(result=>{
      if(result[0]){
        this.entitybyCountry = result[0];
        if (result[1].length>0 && this.currentTabIndex === 0) {
          this.prepareEntityData(result[1]);
        }
        if (result[2].length>0 && this.currentTabIndex === 1) {
          this.prepareEntityDataByGroup(result[2]);
        }
      }

    });
    /*this.store.select(getEntityChartSettings).pipe(takeUntil(this.ngUnsubscribe)).subscribe((entityChartSettings: EntitySetting[]) => {
      if (!entityChartSettings) {
        return;
      }
      if (this.currentTabIndex === 0) {
        this.prepareEntityData(entityChartSettings);
      }
    });*/
    this.store.select(getChangeReportEntityFilterByGroup).pipe(distinctUntilChanged()).subscribe((entitiesByGroup: EntitySettingGroupModel[]) => {
      if (this.currentTabIndex === 1) {
        this.prepareEntityDataByGroup(entitiesByGroup);
      }
    });
  }

  prepareEntityData(entitySettings: EntitySetting[]) {
    this.entities = entitySettings;
    const countrySelectorNodes = this.buildCountrySelectorTree(entitySettings, 0);
    this.countrySelectorData$.next(countrySelectorNodes);
  }

  private prepareEntityDataByGroup(entitiesByGroup: EntitySettingGroupModel[]) {
    this.entitiesByGroup = entitiesByGroup;
    const topicSelectorNodes = this.buildEntityByGroupSelectorTree(entitiesByGroup, 0);
    this.countrySelectorData$.next(topicSelectorNodes);
  }

  buildEntityByGroupSelectorTree(entityListByGroup: any[], level: number) {
    const data: TreeItemNode[] = [];
    entityListByGroup.forEach(item => {
      const node = new TreeItemNode();
      const title = (item.region ? item.region : item.entity.title);
      node.item = item.entitySettings ? {...item, title: title} : {
        ...item.entity,
        proposedEntityCount: this.entitybyCountry.hasOwnProperty(item.entity.country.code)?this.entitybyCountry[item.entity.country.code].proposedCount: 0,
        actualEntityCount: this.entitybyCountry.hasOwnProperty(item.entity.country.code)?this.entitybyCountry[item.entity.country.code].actualCount: 0,
        isChecked: item.isChecked,
        hasFootprint: item.hasFootprint,
        numberOfTransactions: item.numberOfTransactions
      };
      if (item.entitySettings) {
        node.children = this.buildEntityByGroupSelectorTree(item.entitySettings, level + 1);
        let allChildrenChecked = true;
        for (let x = 0; x < item.entitySettings.length; x++) {
          if (!item.entitySettings[x].isChecked) {
            allChildrenChecked = false;
            break;
          }
        }
        node.item.isChecked = allChildrenChecked;
      }
      data.push(node);
    });
    return data;
  }

  navigateToImportEntities() {
    console.log('ImportEntityChart');
    this.router.navigate(CombineRoute.EntityChartImportRoute);
  }

  resetEntitiesFromProjectSettings() {
    var command = new ResetEntitiesFromProjectSettingsCommand();
    this.store.dispatch(new ResetEntitiesFromProjectSettingsAction(command));
  }

  buildCountrySelectorTree(value: EntitySetting[], level: number) {
    const data: TreeItemNode[] = [];
    value.forEach(k => {
      const node = new TreeItemNode();
      node.item = {};
      node.item.country = k.entity.country;
      node.item.title = k.entity.title;
      node.item.id = k.entity.id;
      node.item.isChecked = k.isChecked;
      node.item.proposedEntityCount=0;
      node.item.actualEntityCount=0;
      if(this.entitybyCountry.hasOwnProperty(k.entity.country.code)){
        node.item.proposedEntityCount = this.entitybyCountry[k.entity.country.code].proposedCount;
        node.item.actualEntityCount = this.entitybyCountry[k.entity.country.code].actualCount;
      }


      data.push(node);
    });
    return data;
  }

  onCountrySelectionChanged(event: SelectionEventModel) {
    console.log('country selection changed', event);
    let command = null;
    if (event.currentTreeNode) {
      let countries: CountryModel[] = [];
      let ids: string[] = [];
      if (event.currentTreeNode.item.entitySettings) {
        countries = event.currentTreeNode.item.entitySettings.map(es => es.entity.country);
        ids = event.currentTreeNode.item.entitySettings.map(es => es.entity.id);
      } else {
        countries = [event.currentTreeNode.item.country];
        ids = [event.currentTreeNode.item.id];
      }
      if (event.currentTreeNode.item.isChecked) {
        command = new EntitySettingsAddEntityCommand(countries);
        this.store.dispatch(new PostEntitySettingsAddEntity(command));
      } else {
        command = new EntitySettingsDeleteEntityCommand(ids);
        this.store.dispatch(new PostEntitySettingsDeleteEntity(command));
      }
    } else {
      if (event.selectedTreeNodes) {
        if (event.isCurrentTreeNodeChecked) {
          const countries = this.entities.map(entitySetting => entitySetting.entity.country);
          command = new EntitySettingsAddEntityCommand(countries);
          this.store.dispatch(new PostEntitySettingsAddEntity(command));
        } else {
          const entityIds = this.entities.map(entitySetting => entitySetting.entity.id);
          command = new EntitySettingsDeleteEntityCommand(entityIds);
          this.store.dispatch(new PostEntitySettingsDeleteEntity(command));
        }
      }
    }
  }

  onCountryQuickSelectionClick(event: QuickSelectionModel) {
    console.log('country quick selection clicked', event);
  }

  importEntities() {
    this.router.navigate(CombineRoute.EntityChartImportRoute);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onTabChanged(event: MatTabChangeEvent) {
    this.currentTabIndex = event.index;
    switch (event.index) {
      case 0:
        this.store.dispatch(new GetEntitySettingsAction());
        break;
      case 1:
        this.store.dispatch(new GetEntityFilterGroupByRegionAction());
        break;
    }
  }
}
